<script setup lang="ts">
/**
 * Участники проекта — макет 84:233: поиск сотрудника с ролью и кнопкой, список
 * строк 44 и подпись про роли.
 *
 * Справочник сотрудников живёт в auth и отдаётся страницами без поиска, поэтому
 * его тянут разом и ищут на месте. Права `users:read` есть не у всех, и отказ
 * здесь не молчит: поле само подписывает, почему в нём пусто — искать причину
 * в другом месте человеку неоткуда.
 *
 * Поиск ведёт AppUserPicker из дизайн-системы (DEV-1591): он показывает роль
 * рядом с именем, и двух Михаилов наконец видно порознь. Агентов, партнёров и
 * сервисные учётки справочник трекера не отдаёт вовсе.
 */
import { AppAvatar, AppButton, AppSelect, AppUserPicker } from '@vinprop/design-system'
import { X } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import InlinePicker from '@/components/InlinePicker.vue'
import { authApi } from '@/features/auth/api'
import type { Person } from '@/features/auth/types'

import { MEMBER_ROLES } from '../api'
import { useSettingsStore } from '../store'

import type { V1MemberRole } from '@/api/clients/tracker-project'

const { t, te } = useI18n()
const store = useSettingsStore()

const pickedId = ref('')
const role = ref<V1MemberRole>('MEMBER_ROLE_MEMBER')
const people = ref<Person[]>([])
const isDirectoryOpen = ref(true)

const roleOptions = computed(() =>
  MEMBER_ROLES.map((value) => ({ value, label: t(`settings.members.roles.${value}`) })),
)

onMounted(async () => {
  try {
    people.value = await authApi.listUsers()
  } catch {
    // Нет права на справочник — поле остаётся, но кандидатов в нём не будет.
    isDirectoryOpen.value = false
  }
})

const inProject = computed(() => new Set(store.members.map((member) => member.userId)))

/**
 * Подпись роли: берём первую — их у человека почти всегда одна, а список из
 * трёх ключей в строке подсказки читать некому. Незнакомый ключ показываем
 * как есть: новая роль в auth не повод молчать о ней здесь.
 */
const roleLabel = (person: Person) => {
  const key = person.roles[0]

  if (!key) return ''

  return te(`settings.members.directoryRoles.${key}`)
    ? t(`settings.members.directoryRoles.${key}`)
    : key
}

const candidates = computed(() =>
  people.value
    .filter((person) => !inProject.value.has(person.id))
    .map((person) => ({
      id: person.id,
      name: person.name,
      email: person.email,
      role: roleLabel(person),
    })),
)

const add = () => {
  const person = people.value.find((candidate) => candidate.id === pickedId.value)

  if (!person) return

  store.addMember({ userId: person.id, name: person.name, email: person.email, role: role.value })
  pickedId.value = ''
}
</script>

<template>
  <div class="mtab">
    <div class="mtab__add">
      <div class="mtab__search">
        <AppUserPicker
          v-model="pickedId"
          :users="candidates"
          :placeholder="t('settings.members.searchPlaceholder')"
          :empty-text="t('settings.members.nobody')"
          :unavailable="!isDirectoryOpen"
          :unavailable-text="t('settings.members.noDirectory')"
        />
      </div>

      <AppSelect
        v-model="role"
        class="mtab__role"
        :options="roleOptions"
        :aria-label="t('settings.members.role')"
        size="small"
      />

      <AppButton variant="primary" :disabled="!pickedId" @click="add">
        {{ t('settings.members.add') }}
      </AppButton>
    </div>

    <p class="mtab__overline">
      {{ t('settings.members.title') }} · {{ store.members.length }}
    </p>

    <div class="mtab__card">
      <div v-for="member in store.members" :key="member.userId" class="mrow">
        <AppAvatar :name="member.name" :size="24" />
        <span class="mrow__name">{{ member.name || member.userId }}</span>
        <span class="mrow__mail">{{ member.email }}</span>
        <span class="mrow__spacer" />

        <InlinePicker
          align="right"
          :options="roleOptions"
          :model-value="member.role"
          :empty-text="t('toolbar.filters.empty')"
          @update:model-value="store.setRole(member.userId, $event as V1MemberRole)"
        >
          <template #trigger="{ toggle }">
            <button type="button" class="mrow__role" @click="toggle()">
              {{ t(`settings.members.roles.${member.role || 'MEMBER_ROLE_MEMBER'}`) }} ▾
            </button>
          </template>
        </InlinePicker>

        <!-- Крестик появляется на наведении: в макете его нет вовсе, а убрать
             человека из проекта нужно (DEV-1501). -->
        <button
          type="button"
          class="mrow__off"
          :aria-label="t('settings.members.remove', { name: member.name || member.userId })"
          @click="store.removeMember(member.userId)"
        >
          <X :size="14" />
        </button>
      </div>
    </div>

    <p class="mtab__hint">{{ t('settings.members.hint') }}</p>
  </div>
</template>

<style scoped>
.mtab {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mtab__add {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mtab__search {
  width: 416px;
}

.mtab__role {
  width: 156px;
}

.mtab__overline {
  margin: -8px 0 -12px;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.mtab__card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.mrow {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 44px;
  padding: 0 16px;
  border-top: 1px solid var(--color-border);
}

.mrow:first-child {
  border-top: none;
}

.mrow__name {
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
}

.mrow__mail {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.mrow__spacer {
  flex: 1;
}

.mrow__off {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 8px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  opacity: 0;
  cursor: pointer;
}

.mrow:hover .mrow__off,
.mrow__off:focus-visible {
  opacity: 1;
}

.mrow__off:hover {
  color: var(--color-danger);
}

.mrow__role {
  padding: 4px 10px;
  border: none;
  border-radius: 7px;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
}

.mtab__hint {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
